import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RotateCcw, Search } from 'lucide-react';
import { api, type EventRow, type PipelineRow } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

type Status = 'all' | 'pending' | 'processing' | 'done' | 'failed' | 'dead';

const STATUS_OPTIONS: Array<{ value: Status; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'done', label: 'Done' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead', label: 'Dead' },
];

export function Events(): JSX.Element {
  const [status, setStatus] = useState<Status>('all');
  const [pipelineId, setPipelineId] = useState<string>('all');
  const queryClient = useQueryClient();

  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines'],
    queryFn: () => api.get<PipelineRow[]>('/admin/pipelines'),
  });

  const queryString = (() => {
    const p = new URLSearchParams({ limit: '100' });
    if (status !== 'all') p.set('status', status);
    if (pipelineId !== 'all') p.set('pipeline_id', pipelineId);
    return p.toString();
  })();

  const { data, isLoading } = useQuery({
    queryKey: ['events', status, pipelineId],
    queryFn: () => api.get<{ events: EventRow[]; next_cursor: string | null }>(`/admin/events?${queryString}`),
    refetchInterval: 5_000,
  });

  const retryMutation = useMutation({
    mutationFn: (id: string) => api.post(`/admin/events/${id}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['events'] }),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Events</h1>
        <p className="text-sm text-muted-foreground">
          Лента webhook-событий и их статусы. Обновляется каждые 5 секунд.
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 pt-6">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Фильтры:</span>
          </div>

          <div className="flex flex-wrap gap-1">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatus(opt.value)}
                className={`rounded-md border px-3 py-1 text-xs font-medium transition-colors ${
                  status === opt.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="ml-auto">
            <select
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              className="flex h-9 rounded-md border border-input bg-background px-3 text-xs"
            >
              <option value="all">Все pipelines</option>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <TableSkeleton
          columns={['ID', 'Pipeline', 'Event', 'Lead', 'Status', 'Try', 'ms', 'Received', '']}
          rows={5}
        />
      ) : (
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">ID</TableHead>
              <TableHead>Pipeline</TableHead>
              <TableHead>Event</TableHead>
              <TableHead className="w-24">Lead</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-16 text-right">Try</TableHead>
              <TableHead className="w-20 text-right">ms</TableHead>
              <TableHead className="w-40">Received</TableHead>
              <TableHead className="w-12 text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!isLoading && data?.events.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                  Событий нет под выбранным фильтром.
                </TableCell>
              </TableRow>
            )}
            {data?.events.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-xs text-muted-foreground">#{e.id}</TableCell>
                <TableCell className="max-w-48 truncate">{e.pipeline_name}</TableCell>
                <TableCell className="text-xs">
                  <code className="rounded bg-muted px-1.5 py-0.5">{e.kommo_event}</code>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {e.kommo_entity_id ?? '—'}
                </TableCell>
                <TableCell>
                  <StatusBadge status={e.status} />
                  {e.last_error && (
                    <span
                      className="ml-1 inline-flex"
                      title={e.last_error}
                    >
                      <AlertCircle className="h-3 w-3 text-destructive" />
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right text-xs">{e.attempts}</TableCell>
                <TableCell className="text-right text-xs">{e.duration_ms ?? '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(e.received_at).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {(e.status === 'failed' || e.status === 'dead') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Retry"
                      onClick={() => retryMutation.mutate(e.id)}
                      disabled={retryMutation.isPending}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: EventRow['status'] }): JSX.Element {
  switch (status) {
    case 'done':
      return <Badge variant="success">done</Badge>;
    case 'failed':
      return <Badge variant="destructive">failed</Badge>;
    case 'dead':
      return <Badge variant="destructive">dead</Badge>;
    case 'processing':
      return <Badge variant="warning">processing</Badge>;
    case 'pending':
      return <Badge variant="secondary">pending</Badge>;
  }
}
